import type { EntryCta, SubmitTransition } from '@funkit/connect-core'
import { enteredStep, submittableTransition } from '@funkit/connect-core'
import type { TFunction } from 'i18next'

/**
 * Copy for a server-emitted CTA. `id` is the localization key (known ids get
 * real translations), `labelFallback` covers ids this build predates — the
 * contract's own fallback rule, so a new detour ships without a client
 * release.
 */
export function ctaLabel(cta: SubmitTransition, t: TFunction): string {
  const known: Record<string, string | undefined> = {
    continue: t('common.continue'),
    confirm: t('common.confirm'),
    verify: t('common.continue'),
    escalate: t('common.continue'),
    submit_round: t('common.continue'),
    kyc_required: t('fiat.kycRequired'),
    retry_payment: t('common.tryAgain'),
    requote: t('fiat.requote'),
  }
  return known[cta.id] ?? cta.labelFallback ?? cta.id
}

/** Ids the contract uses for the detour through identity. */
const IDENTITY_DETOUR_IDS = new Set(['kyc_required', 'verify_identity'])

/**
 * Whether the quote is holding the deposit on identity, which the entry
 * screen answers with a banner as well as a relabelled button.
 *
 * A round the endpoint answered with says so by its state; a quote that will
 * price once identity clears says so by its transition id.
 */
export function isIdentityDetour(entry: EntryCta): boolean {
  const step = enteredStep(entry)
  if (step !== undefined) {
    return step.state.kind === 'KYC'
  }
  const transition = submittableTransition(entry)
  return transition !== undefined && IDENTITY_DETOUR_IDS.has(transition.id)
}

/**
 * The words on the amount screen's primary button.
 *
 * Paying is the app's own call to word — it owns that copy and its branding.
 * Every other step is the server's to name, which is what lets a step this
 * build has never seen still render sensibly. The screen asks this rather
 * than inspecting `kind`, so intent stays separate from presentation.
 *
 * A round is the exception: it arrived with no transition of its own to name
 * it, so the state it holds the deposit on is what the button says.
 */
export function entryCtaLabel(
  entry: EntryCta,
  t: TFunction,
  appLabel: string,
): string {
  if (entry.kind === 'CONTINUE') {
    return isIdentityDetour(entry)
      ? t('fiat.kycRequired')
      : t('common.continue')
  }
  const transition = submittableTransition(entry)
  if (entry.kind === 'PAY' || transition === undefined) {
    return appLabel
  }
  return ctaLabel(transition, t)
}
